/**
 * @fileoverview フロントバリデーション関連のデバッグ用のインスペクタコンポーネント
 * @description ValidationObserver を読み込んでいるページにおいて、development モード時に URL に「?mode=debug」というクエリを付与すると簡易的なバリデーション状況が表示されます。
 * @description 主に、バリデーション関連でうまく動作していない場合等において、どこの項目がバリデーション通過していないのか、等の確認のために利用できます。
 */
<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { useValidationStore } from '@/stores/validation'
import { UrlUtil } from '@/utilities/url'

export default defineComponent({
  name: 'ValidationInspector',

  setup() {
    // store
    const validationStore = useValidationStore()

    const inspectorPosition = ref<'left' | 'right'>('left')

    const hasInspector = computed<boolean>(() => {
      const url = new UrlUtil()
      return (
        import.meta.env.MODE !== 'production' &&
        url.getQueryParameter('mode') === 'debug' &&
        !!validationStore.context.queue.length)
    })

    const uncheckedFields = (namespace: string) => {
      const queue = validationStore.getQueue(namespace)
      if (!queue) return []
      const unchecked = queue.fields.filter(item => !queue.checkedFields.includes(item))
      return unchecked.length !== queue.fields.length ? unchecked : []
    }

    return {
      // store
      validationStore,

      // data
      inspectorPosition,

      // computed
      hasInspector,

      // methods
      uncheckedFields,
    }
  }
})
</script>

<template>
  <section
    v-if="hasInspector"
    class="f-section__validationInspector">
    <div class="f-section__validationInspector--body">
      <div class="o-list__cols">
        <ul class="o-list__cols--items">
          <li class="o-list__cols--item">
            <div
              class="o-action__inspectorPosition"
              data-position="left"
              :data-active="inspectorPosition === 'left'">
              <button
                class="o-action__inspectorPosition--item"
                title="左に表示"
                :disabled="inspectorPosition === 'left'"
                @click="inspectorPosition = 'left'">
                左
              </button>
            <!-- /.o-action__inspectorPosition --></div>
          </li>

          <li class="o-list__cols--item">
            <div
              class="o-action__inspectorPosition"
              data-position="right"
              :data-active="inspectorPosition === 'right'">
              <button
                class="o-action__inspectorPosition--item"
                title="右に表示"
                :disabled="inspectorPosition === 'right'"
                @click="inspectorPosition = 'right'">
                右
              </button>
            <!-- /.o-action__inspectorPosition --></div>
          </li>
        </ul>
      <!-- /.o-list__cols --></div>

      <div
        class="div"
        :style="{
          left: inspectorPosition === 'left' ? '0' : '100%',
          transform: inspectorPosition === 'left' ? 'translateX(0)' : 'translateX(-100%)',
        }">
        <div
          v-for="validationQueue in validationStore.context.queue"
          :key="validationQueue.namespace"
          class="l-unit__validationInspector"
          :data-active="validationStore.context.progressNamespace === validationQueue.namespace">
          <div class="l-unit__validationInspector--header">
            <div class="o-list__record is-colon u-text__xx-small">
              <dl class="o-list__record--unit">
                <dt class="o-list__record--term">
                  namespace
                </dt>
                <dd class="o-list__record--description">
                  {{ validationQueue.namespace }}
                </dd>
              </dl>
            <!-- /.o-list__record --></div>
          <!-- /.l-unit__validationInspector header --></div>

          <div class="l-unit__validationInspector--description">
            <div class="o-list__record is-colon u-text__small">
              <dl class="o-list__record--unit">
                <dt class="o-list__record--term">
                  in progress
                </dt>
                <dd class="o-list__record--description">
                  {{ validationQueue.run }}
                </dd>
              </dl>
            <!-- /.o-list__record --></div>

            <div class="o-list__debugger u-text__xx-small">
              <dl class="o-list__debugger--unit">
                <dt class="o-list__debugger--term">
                  input scheme
                </dt>
                <dd class="o-list__debugger--description">
                  <ul
                    v-if="validationQueue.fields.length"
                    class="ul">
                    <li
                      v-for="field in validationQueue.fields"
                      :key="field"
                      class="li">
                      <span
                        :class="[
                          'span',
                          {
                            'u-text__emphasis': validationQueue.checkedFields.includes(field),
                            'u-text__bold': validationQueue.checkedFields.includes(field),
                          }
                        ]">
                        {{ field || '' }}
                      </span>
                    </li>
                  </ul>
                  <p
                    v-else
                    class="p">
                    no data
                  </p>
                </dd>
              </dl>

              <dl class="o-list__debugger--unit">
                <dt class="o-list__debugger--term">
                  unchecked fields
                </dt>
                <dd class="o-list__debugger--description">
                  <ul
                    v-if="uncheckedFields(validationQueue.namespace).length"
                    class="ul">
                    <li
                      v-for="(field, index) in uncheckedFields(validationQueue.namespace)"
                      :key="index"
                      class="li">
                      {{ field || '' }}
                    </li>
                  </ul>
                  <p
                    v-else
                    class="p">
                    no data
                  </p>
                </dd>
              </dl>
            <!-- /.o-list__debugger --></div>
          <!-- /.l-unit__validationInspector description --></div>

          <div class="l-unit__validationInspector--action">
            <div class="o-action__toggle">
              <label class="o-action__toggle--item">
                <input
                  class="o-action__toggle--checkbox"
                  type="checkbox"
                  checked>
              </label>
            <!-- /.o-action__toggle --></div>
          <!-- /.l-unit__validationInspector action --></div>
        <!-- /.l-unit__validationInspector --></div>
      </div>
    <!-- /.f-section__validationInspector body --></div>
  <!-- /.f-section__validationInspector --></section>
</template>

<style lang="sass" scoped>
$toggle-button-area-size: 1em
$toggle-button-size: 0.5em
$position-button-size: 1em
$section-padding: 0.5em
$position-button-gap: 1em

// foundation
.f-section__validationInspector
  position: fixed
  top: 0
  left: 0
  overflow: auto
  color: $font-white
  z-index: 1000

  & &--body
    padding: $section-padding

// layout
.l-unit__validationInspector
  $root: &
  display: grid
  grid-template-columns: 1fr
  grid-template-rows: 1em 0fr
  align-items: start
  position: relative
  overflow: hidden
  margin-top: $position-button-gap
  padding: 1em
  background-color: rgba($bg-black, 0.5)
  border-radius: $border-radius
  +transition($duration: $transition-middle)

  &::before
    content: ''
    width: 100%
    height: 100%
    display: block
    position: absolute
    top: 0
    left: 0
    border: 3px solid $border-orange
    border-radius: $border-radius
    opacity: 0
    box-sizing: border-box
    +transition

  &[data-active="true"]
    &::before
      opacity: 1

  &:first-child
    margin-top: 0

  & &--header
    grid-column: 1 / 2
    grid-row: 1 / 2
    align-self: center
    overflow: hidden
    white-space: nowrap
    margin-right: ($toggle-button-area-size + 0.5em)

  & &--description
    grid-column: 1 / 2
    grid-row: 2 / 3
    overflow: hidden

    // margin、または padding で header との間の隙間を空けると、grid アコーディオンの表示計算が狂うため、疑似要素で代替
    &::before
      content: ''
      height: 1em
      display: block

  & &--action
    width: $toggle-button-area-size
    height: $toggle-button-area-size
    position: absolute
    top: 1em
    right: 1em

  // relation
  &:has(.o-action__toggle--checkbox:checked)
    grid-template-rows: 1em 1fr

// object
.o-list__debugger
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--unit
    margin-top: 1em

    &:first-child
      margin-top: 0

  & &--term
    font-weight: bold

  & &--description
    padding-left: 1.5em

.o-action__toggle
  $root: &
  width: 100%
  height: 100%

  & &--item
    width: 100%
    height: 100%
    display: block
    position: relative

    &::before
      content: ''
      width: $toggle-button-size * 3
      height: $toggle-button-size * 3
      display: block
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      background-color: rgba($bg-black, 0)
      border-radius: 50%
      +transition

    &::after
      +iconArrow($direction: down, $color: $border-white, $size: $toggle-button-size, $weight: 0.12em)
      display: block
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%) rotate(45deg)
      margin-top: -0.1em
      cursor: pointer
      +transition

    +mouseover
      &::before
        background-color: rgba($bg-black, 0.2)

  & &--checkbox
    width: 0
    height: 0
    display: block
    overflow: hidden
    color: rgba($font-white, 0)
    white-space: nowrap
    border: none

  &:has(.o-action__toggle--checkbox:checked)
    > #{$root}--item::after
      transform: translate(-50%, -50%) rotate(-135deg)
      margin-top: 0.15em

.o-action__inspectorPosition
  $root: &
  $border-weight: 2px

  & &--item
    width: $position-button-size
    height: $position-button-size
    display: block
    position: relative
    overflow: hidden
    color: rgba($font-white, 0)
    white-space: nowrap

    &::before,
    &::after
      content: ''
      height: 100%
      display: block
      position: absolute
      top: 50%
      transform: translateY(-50%)
      box-sizing: border-box
      +transition

    &::before
      width: 100%
      left: 0
      border: $border-weight solid $border-black
      border-radius: 3px

  &[data-position="left"]
    #{$root}--item
      &::after
        width: 0
        left: 30%
        border-left: $border-weight solid $border-black

  &[data-position="right"]
    #{$root}--item
      &::after
        width: 0
        right: 30%
        border-left: $border-weight solid $border-black

  &[data-active="true"]
    #{$root}--item
      &::before
        border-color: $border-dark-orange

      &::after
        border-color: $border-dark-orange

  +mouseover
    #{$root}--item:not(:disabled)
      &::before
        border-color: $border-light-cyan

      &::after
        border-color: $border-light-cyan

// override
.o-list__cols
  position: relative
  z-index: 1

.o-list__cols + .div
  width: 320px
  max-height: 100%
  position: fixed
  top: 0
  overflow: auto
  color: $font-white
  margin-top: 0
  padding:
    top: $position-button-size + $section-padding + $position-button-gap
    left: $section-padding
    right: $section-padding
    bottom: $section-padding
  +transition
</style>
